import { FC, Fragment, memo, useMemo, useState } from 'react'
import { Control } from 'react-hook-form'
import {
  ColumnDef,
  ExpandedState,
  TableOptions,
  getCoreRowModel,
  getExpandedRowModel,
} from '@tanstack/react-table'
import DataSource from 'devextreme/data/data_source'
import MaterialSymbolsArrowRight from '~icons/material-symbols/arrow-right'
import ExpandMore from '~icons/material-symbols/expand-more'
import ChevronRight from '~icons/material-symbols/chevron-right'

import { TaskPriorities } from 'enums/app'
import { DatePickerDateFormat } from 'enums/system'

import { TaskKindRef, UserRef } from '@crew/models/refs'
import { useTranslation } from '@crew/modules/i18n'
import { getEnumDataByValue } from '@crew/utils/enum'

import { CrewBadge } from 'components/elements/crewBadge/crewBadge'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewTaskPriority } from 'components/elements/crewTaskPriority/crewTaskPriority'
import { CrewTable } from 'components/elements/crewTable/crewTable'
import { CrewDatePickerField } from 'components/forms/crewDatePickerField'
import { CrewSelectBoxField } from 'components/forms/crewSelectBoxField'
import { CrewBadgeSelectBoxField } from 'components/forms/crewBadgeSelectBoxField'

import {
  FormValues,
  TemplateTaskViewModel,
} from '../../useTaskEntryByTemplateForm'

export type TaskTemplateTaskPanelProps = {
  control: Control<FormValues>
  tasks: TemplateTaskViewModel[]
  mappingRequiredAssignToUsers: UserRef[]
  mappingRequiredTaskKinds: TaskKindRef[]
  userDataSource: DataSource
  taskKindDataSource: DataSource
  hasMappingError: boolean
  isTemplateUnselected: boolean // テンプレートが未選択かどうか
}

type TaskTreeNode = TemplateTaskViewModel & { subRows: TaskTreeNode[] }

// タスクテンプレートタスクパネル
export const TaskTemplateTaskPanel: FC<TaskTemplateTaskPanelProps> = memo(
  (props) => {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState<ExpandedState>({})

    // マッピング設定領域を表示するかどうか
    const showMapping =
      props.mappingRequiredAssignToUsers.length > 0 ||
      props.mappingRequiredTaskKinds.length > 0

    // ツリー構造をCrewTable用に整形
    const treeData = useMemo<TaskTreeNode[]>(() => {
      const byId = new Map<string, TaskTreeNode>()
      props.tasks.forEach((task) => {
        byId.set(task.id, { ...task, subRows: [] })
      })

      const roots: TaskTreeNode[] = []
      byId.forEach((task) => {
        if (task.parentTaskTemplateTaskId) {
          const parent = byId.get(task.parentTaskTemplateTaskId)
          if (parent) {
            parent.subRows.push(task)
          } else {
            roots.push(task)
          }
        } else {
          roots.push(task)
        }
      })

      // taskOrder順で並べ替え
      const sortRecursive = (nodes: TaskTreeNode[]) => {
        nodes.sort((a, b) => a.taskOrder.localeCompare(b.taskOrder))
        nodes.forEach((n) => sortRecursive(n.subRows))
      }
      sortRecursive(roots)

      return roots
    }, [props.tasks])

    // タスクテンプレートタスク一覧のカラム定義
    const columns = useMemo<ColumnDef<TaskTreeNode>[]>(
      () => [
        // タスク件名
        {
          id: 'subject',
          accessorKey: 'subject',
          header: t('label.taskSubject'),
          cell: ({ row, getValue }) => {
            const canExpand = row.getCanExpand()
            const isExpanded = row.getIsExpanded()
            const subject = getValue<string>()
            return (
              <div
                className="flex items-center gap-2"
                style={{ paddingLeft: row.depth * 16 }}
              >
                {/* 展開/折りたたみボタン */}
                {canExpand ? (
                  <button
                    type="button"
                    onClick={row.getToggleExpandedHandler()}
                    className="flex items-center"
                  >
                    {isExpanded ? (
                      <ExpandMore width={16} height={16} />
                    ) : (
                      <ChevronRight width={16} height={16} />
                    )}
                  </button>
                ) : (
                  // 展開/折りたたみボタンのスペース確保
                  <span className="w-4" />
                )}
                <span className="truncate">{subject}</span>
              </div>
            )
          },
          size: 320,
          minSize: 220,
        },
        // 種別
        {
          id: 'taskKind',
          accessorKey: 'taskKind',
          header: t('label.taskKindName'),
          cell: ({ row }) => (
            <div className="w-full flex justify-center">
              <CrewBadge displayColor={row.original.taskKind.displayColor}>
                {row.original.taskKind.name}
              </CrewBadge>
            </div>
          ),
          size: 120,
          minSize: 100,
        },
        // 優先度
        {
          id: 'taskPriority',
          accessorKey: 'taskPriority',
          header: t('label.taskPriority'),
          cell: ({ row }) => {
            const priority = getEnumDataByValue(
              TaskPriorities,
              row.original.taskPriority
            )
            return (
              <div className="flex justify-center">
                {priority && (
                  <CrewTaskPriority
                    taskPriority={priority.value}
                    className="justify-center"
                  />
                )}
              </div>
            )
          },
          size: 110,
          minSize: 100,
        },
        // 担当者
        {
          id: 'assignToUser',
          accessorKey: 'assignToUser',
          header: t('label.assignedUser'),
          cell: ({ row }) => (
            <div className="flex justify-center">
              {row.original.assignToUser ? (
                <CrewUserAvatar
                  userId={row.original.assignToUser.id}
                  displayName={row.original.assignToUser.displayName}
                  cacheValue={row.original.assignToUser.displayName}
                  size={CrewAvatarSize.xs}
                  showLabel={true}
                  disableClick={true}
                />
              ) : (
                <span>-</span>
              )}
            </div>
          ),
          size: 160,
          minSize: 130,
        },
        // 所要日数
        {
          id: 'requiredDays',
          accessorKey: 'requiredDays',
          header: t('label.taskTemplate.requiredDays'),
          cell: ({ row }) => (
            <div className="text-center">
              {row.original.requiredDays !== null &&
              row.original.requiredDays !== undefined
                ? t('label.taskTemplate.valueOfRequiredDays', {
                    value: row.original.requiredDays,
                  })
                : '-'}
            </div>
          ),
          size: 100,
          minSize: 90,
        },
      ],
      [t]
    )

    // テーブルオプション
    const tableOptions: TableOptions<TaskTreeNode> = useMemo(
      () => ({
        data: treeData,
        columns,
        columnResizeMode: 'onChange',
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row) => row.subRows ?? [],
        state: {
          expanded,
        },
        onExpandedChange: setExpanded,
        enableSorting: false,
        meta: {
          headerRowHeight: 32,
          dataRowHeight: 32,
        },
      }),
      [columns, expanded, treeData]
    )

    // タスクテンプレートタスク一覧のテーブルに充てるクラス
    // マッピングが必要かどうかに応じてcssを切り替える
    const tableContainerClass = showMapping
      ? 'flex max-h-[360px] overflow-y-auto'
      : 'flex max-h-[640px] overflow-y-auto'

    return (
      <div className="h-full flex flex-col gap-y-1">
        {/* 基準日 */}
        <div className="flex">
          <CrewDatePickerField
            id="task-template-base-date"
            name="baseDate"
            control={props.control}
            label={t('label.taskTemplate.referenceDate')}
            displayFormat={DatePickerDateFormat.YYYYMMDD}
            calendarOptions={{ firstDayOfWeek: 1 }}
          />
        </div>
        {/* タスクテンプレートタスク一覧 */}
        <div className="flex flex-col gap-y-2">
          {props.isTemplateUnselected ? (
            // テンプレートが選択されていない場合はその旨のメッセージを表示
            <div>{t('label.taskTemplate.pleaseSelectTaskTemplate')}</div>
          ) : props.tasks.length === 0 ? (
            // テンプレートタスクが一件もない場合はその旨のメッセージを表示
            <div>{t('label.taskTemplate.hasNoTasks')}</div>
          ) : (
            // テンプレートタスク一覧表示
            <div className={tableContainerClass}>
              <CrewTable
                tableOptions={tableOptions}
                showPager={false}
                showColumnSelector={false}
              />
            </div>
          )}

          {/* マッピングが必要な項目がある場合、マッピングを設定する領域を表示 */}
          {showMapping && (
            <div className="flex flex-col gap-y-2">
              {/* 「項目のマッピング」とエラーメッセージ表示 */}
              <div className="flex flex-col gap-y-1">
                <div className="font-bold">
                  {t('label.taskTemplate.mappingToValidValue')}
                </div>
                {props.hasMappingError && (
                  <div className="crew-error-text">
                    {t(
                      'label.taskTemplate.pleaseInputValueBecauseInvalidValue'
                    )}
                  </div>
                )}
              </div>

              {/* マッピング用の入力欄 */}
              <div className="max-h-56 overflow-y-auto flex w-auto">
                <div className="grid grid-cols-[auto_auto_1fr] gap-y-1 gap-x-2.5 items-center">
                  {/* ラベル行 */}
                  <div className="font-semibold">
                    {t('label.taskTemplate.copySource')}
                  </div>
                  <div />
                  <div className="font-semibold">
                    {t('label.taskTemplate.copyDestination')}
                  </div>

                  {/* ユーザーのマッピング */}
                  {props.mappingRequiredAssignToUsers.map((user) => (
                    // key指定したいので、 <> ではなく Fragment とする
                    <Fragment key={`mapping-user-${user.id}`}>
                      <div className="min-w-[220px] flex">
                        <CrewUserAvatar
                          userId={user.id}
                          displayName={user.displayName}
                          cacheValue={user.displayName}
                          size={CrewAvatarSize.xs}
                          showLabel={true}
                          disableClick={true}
                        />
                      </div>

                      <MaterialSymbolsArrowRight width={24} height={24} />

                      <div className="min-w-[220px]">
                        <CrewSelectBoxField
                          id={`mapping-user-${user.id}`}
                          name={`userMappings.${user.id}` as const}
                          control={props.control}
                          dataSource={props.userDataSource}
                          valueExpr="id"
                          displayExpr="displayName"
                          searchEnabled={true}
                          minSearchLength={0}
                        />
                      </div>
                    </Fragment>
                  ))}

                  {/* タスク種別のマッピング */}
                  {props.mappingRequiredTaskKinds.map((taskKind) => (
                    // key指定したいので、 <> ではなく Fragment とする
                    <Fragment key={`mapping-kind-${taskKind.id}`}>
                      <div className="min-w-[220px] flex">
                        <CrewBadge displayColor={taskKind.displayColor}>
                          {taskKind.name}
                        </CrewBadge>
                      </div>

                      <MaterialSymbolsArrowRight width={24} height={24} />

                      <div className="min-w-[220px]">
                        <CrewBadgeSelectBoxField
                          id={`mapping-kind-${taskKind.id}`}
                          name={`taskKindMappings.${taskKind.id}` as const}
                          control={props.control}
                          dataSource={props.taskKindDataSource}
                          valueExpr="id"
                          displayExpr="name"
                          showClearButton={false}
                          showLabel={false}
                        />
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
)
